import React, { useState } from 'react'
import styled from 'styled-components'
import { Table, Pagination } from 'antd'
import { CheckOutlined } from '@ant-design/icons'

const StyledTable = styled(Table)`
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;

  .ant-table {
    background: #fff;
    border-radius: 8px;
  }

  .ant-table-thead > tr > th {
    text-align: center;
    padding: 10px;
    background-color: #f5f5f5;
    font-weight: bold;
    border-bottom: 2px solid #ddd;
    border-right: 1px solid #ddd;
  }

  .ant-table-thead > tr > th:first-child {
    border-right: 3px solid #333;
  }

  .ant-table-thead > tr > th:last-child {
    border-right: none;
  }

  .ant-table-tbody > tr > td {
    text-align: center;
    padding: 10px;
    border-bottom: 1px solid #ddd;
    border-right: 1px solid #ddd;
  }

  .ant-table-tbody > tr > td:first-child {
    border-right: 3px solid #333;
  }

  .ant-table-tbody > tr > td:last-child {
    border-right: none;
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: #fafafa;
  }

  .ant-table-tbody > tr:hover {
    background-color: #e6f7ff;
    transition: background 0.3s ease-in-out;
  }

  .ant-table-tbody > tr.section-header {
    background-color: #f6ffed !important;
    border-top: 2px solid #b4d820;
  }

  .ant-table-tbody > tr.section-header:hover {
    background-color: #f6ffed !important;
  }

  .ant-table-tbody > tr.section-header > td {
    border-bottom: 1px solid #b4d820;
    font-weight: bold;
  }
`

const defaultAllergens = [
  'Celery',
  'Gluten',
  'Crustaceans',
  'Eggs',
  'Fish',
  'Lupin',
  'Milk',
  'Molluscs',
  'Mustard',
  'Nuts',
  'Peanuts',
  'Sesame seeds',
  'Soya',
  'Sulphites',
]

const AllergenGrid = ({
  dishes,
  businessName,
  isAllRecipes = false,
  pagination = true,
}) => {
  // Always use the complete default allergen list to ensure all headers are shown
  const allergens = defaultAllergens

  // Safety check for dishes - ensure it's an array
  const safeDishes = Array.isArray(dishes) ? dishes : []

  // Debug logging
  // console.log('AllergenGrid - dishes:', safeDishes)
  // console.log('AllergenGrid - allergenTypes:', allergens)
  // console.log('AllergenGrid - isAllRecipes:', isAllRecipes)
  if (safeDishes.length > 0) {
    // console.log('First dish allergens:', safeDishes[0].allergens)
  }

  // Pagination configuration
  const pageSize = 50 // Show 50 dishes per page
  const [currentPage, setCurrentPage] = useState(1)

  // Calculate pagination
  const totalDishes = safeDishes.length
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedDishes = pagination
    ? safeDishes.slice(startIndex, endIndex)
    : safeDishes

  const columns = [
    {
      title: 'Dishes',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 200,
      render: (text, record) => {
        if (record.type === 'section') {
          return (
            <div
              style={{
                fontWeight: '500',
                fontSize: '12px',
                color: '#666666',
                padding: '2px 0',
                borderBottom: '1px solid #e8e8e8',
                backgroundColor: '#f8f8f8',
                lineHeight: '1.1',
              }}
            >
              {text} ({record.recipeCount} recipes)
            </div>
          )
        }
        return text
      },
    },
    ...allergens.map(allergen => {
      const columnKey = allergen.toLowerCase().replace(/\s+/g, '_')
      return {
        title: allergen,
        dataIndex: columnKey,
        key: columnKey,
        align: 'center',
        render: (value, record) => {
          if (record.type === 'section') {
            return '' // Section headers don't show allergen data
          }

          if (value === true) {
            return <CheckOutlined style={{ color: '#52c41a' }} />
          } else if (value === 'traces') {
            return (
              <span style={{ color: '#fa8c16', fontWeight: 'bold' }}>
                May Contain
              </span>
            )
          }
          return null
        },
      }
    }),
  ]

  // Process data - handle both new combined structure and legacy structure
  const data = []

  if (paginatedDishes.length > 0 && paginatedDishes[0].type) {
    // New combined structure with section headers and recipes
    paginatedDishes.forEach((item, index) => {
      if (item.type === 'section') {
        // Section header
        data.push({
          key: `section-${item.sectionId}-${index}`,
          type: 'section',
          name: item.name,
          recipeCount: item.recipeCount,
          ...allergens.reduce((acc, allergen) => {
            acc[allergen.toLowerCase().replace(/\s+/g, '_')] = null
            return acc
          }, {}),
        })
      } else if (item.type === 'recipe') {
        // Recipe row
        data.push({
          key: `recipe-${item.name}-${index}`,
          type: 'recipe',
          name: item.name,
          ...allergens.reduce((acc, allergen) => {
            const columnKey = allergen.toLowerCase().replace(/\s+/g, '_')
            const hasAllergen = item.allergens?.includes(allergen)
            const hasTrace = item.allergenTraces?.includes(allergen)
            acc[columnKey] = hasAllergen ? true : hasTrace ? 'traces' : null
            return acc
          }, {}),
        })
      }
    })
  } else {
    // Legacy structure - group dishes by section
    const dishesBySection = {}
    paginatedDishes.forEach(dish => {
      if (isAllRecipes) {
        // For all recipes view, put all dishes in a single section without header
        const sectionName = 'all-recipes'
        if (!dishesBySection[sectionName]) {
          dishesBySection[sectionName] = []
        }
        dishesBySection[sectionName].push(dish)
      } else if (dish.section) {
        // For menu-specific view, only include dishes with actual sections
        const sectionName = dish.section
        if (!dishesBySection[sectionName]) {
          dishesBySection[sectionName] = []
        }
        dishesBySection[sectionName].push(dish)
      }
    })

    // Create data with section headers for legacy structure
    Object.keys(dishesBySection).forEach(sectionName => {
      // Only add section header if there are dishes in this section
      if (dishesBySection[sectionName].length > 0) {
        // Skip section header for all-recipes view
        if (sectionName !== 'all-recipes') {
          // Add section header row
          data.push({
            key: `section-${sectionName}`,
            name: (
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '16px',
                  color: '#B4D820',
                  backgroundColor: '#f6ffed',
                  padding: '8px',
                  margin: '-8px',
                  borderRadius: '4px',
                }}
              >
                {sectionName}
              </div>
            ),
            isSectionHeader: true,
            ...allergens.reduce(
              (acc, allergen) => ({
                ...acc,
                [allergen.toLowerCase().replace(/\s+/g, '_')]: null,
              }),
              {},
            ),
          })
        }

        // Add dishes for this section
        dishesBySection[sectionName].forEach((dish, index) => {
          // Create a more robust allergen matching
          const dishAllergens = Array.isArray(dish.allergens)
            ? dish.allergens
            : []
          const dishAllergenTraces = Array.isArray(dish.allergenTraces)
            ? dish.allergenTraces
            : []

          // Debug logging for pork dishes
          if (dish.name && dish.name.toLowerCase().includes('pork')) {
            console.log(`Pork dish debug - ${dish.name}:`, {
              allergens: dishAllergens,
              traces: dishAllergenTraces,
              dish: dish,
            })
          }

          // Debug logging for Mince and tatties
          if (dish.name === 'Mince and tatties') {
            console.log('Mince and tatties debug:', {
              allergens: dishAllergens,
              traces: dishAllergenTraces,
              dish: dish,
            })
          }

          // Debug logging for Quick Cocktail
          if (dish.name === 'Quick Cocktail') {
            console.log('Quick Cocktail debug:', {
              allergens: dishAllergens,
              traces: dishAllergenTraces,
              dish: dish,
            })
          }

          const dishData = {
            key: `${sectionName}-${dish.name}-${index}`,
            name: dish.name,
            ...allergens.reduce((acc, allergen) => {
              // Check for "Yes" allergens first
              const hasAllergen = dishAllergens.some(
                dishAllergen =>
                  dishAllergen === allergen ||
                  dishAllergen.toLowerCase() === allergen.toLowerCase() ||
                  dishAllergen.toLowerCase().replace(/\s+/g, '') ===
                    allergen.toLowerCase().replace(/\s+/g, ''),
              )

              // Check for "Traces" allergens
              const hasTraces = dishAllergenTraces.some(
                dishAllergen =>
                  dishAllergen === allergen ||
                  dishAllergen.toLowerCase() === allergen.toLowerCase() ||
                  dishAllergen.toLowerCase().replace(/\s+/g, '') ===
                    allergen.toLowerCase().replace(/\s+/g, ''),
              )

              // "Yes" overrides "Traces"
              const value = hasAllergen ? true : hasTraces ? 'traces' : false

              // Debug logging for Mince and tatties and Lupin
              if (dish.name === 'Mince and tatties' && allergen === 'Lupin') {
                console.log('Mince and tatties Lupin debug:', {
                  allergen,
                  hasAllergen,
                  hasTraces,
                  value,
                  dishAllergens,
                  dishAllergenTraces,
                })
              }

              // Debug logging for Quick Cocktail and Sulphites
              if (dish.name === 'Quick Cocktail' && allergen === 'Sulphites') {
                console.log('Quick Cocktail Sulphites debug:', {
                  allergen,
                  hasAllergen,
                  hasTraces,
                  value,
                  dishAllergens,
                  dishAllergenTraces,
                })
              }

              return {
                ...acc,
                [allergen.toLowerCase().replace(/\s+/g, '_')]: value,
              }
            }, {}),
          }
          data.push(dishData)
        })
      }
    })
  }

  return (
    <div>
      <h2 style={{ textAlign: 'center', marginBottom: '8px' }}>
        {businessName} – Allergen Information
      </h2>
      {pagination && totalDishes > pageSize && (
        <div style={{ marginBottom: '16px', textAlign: 'center' }}>
          <Pagination
            current={currentPage}
            total={totalDishes}
            pageSize={pageSize}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} dishes`
            }
          />
        </div>
      )}
      <StyledTable
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowClassName={record =>
          record.isSectionHeader ? 'section-header' : ''
        }
      />
      {pagination && totalDishes > pageSize && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <Pagination
            current={currentPage}
            total={totalDishes}
            pageSize={pageSize}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} dishes`
            }
          />
        </div>
      )}
      <p
        style={{
          textAlign: 'center',
          marginTop: '12px',
          fontSize: '14px',
          color: '#666',
        }}
      >
        Produced by Orders Made Simple on {new Date().toLocaleDateString()}
      </p>
    </div>
  )
}

export default AllergenGrid
